import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { FaChartPie, FaLayerGroup, FaHourglassHalf, FaUsers, FaUserPlus,   FaFileExcel } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartPie, faChartBar, faUsers, faListAlt, faTicketAlt, faLayerGroup, faUser , faLink} from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx';
import { FaLink } from 'react-icons/fa';



// Helper: generate monthly growth data from members
function generateMemberGrowthData(members) {
  if (!Array.isArray(members)) return [];
  // Group by month
  const counts = {};
  members.forEach(m => {
    if (!m.RegDate) return;
    const d = new Date(m.RegDate);
    if (isNaN(d)) return;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    counts[key] = (counts[key] || 0) + 1;
  });
  // Sort months and accumulate
  const months = Object.keys(counts).sort();
  let cumulative = 0;
  return months.map(month => {
    cumulative += counts[month];
    return { month, count: cumulative };
  });
}

export default function ClubStatistics() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalStudents, setTotalStudents] = useState(0);

  const user = JSON.parse(localStorage.getItem('user')) || {
    name: 'Unknown User',
    role: 'Member',
    image: '/user.png',
  };

  const exportToExcel = (category) => {
    // Prepare data for export
    const firstPrefData = category.firstPref.map((student, index) => ({
      'S.No': index + 1,
      'Name': student.name,
      'Faculty': student.faculty,
      'Year/Semester': student.year,
      'Phone': student.phone,
      'Preference': student.pref,
      'Type': 'First Preference'
    }));

    const secondPrefData = category.secondPref.map((student, index) => ({
      'S.No': firstPrefData.length + index + 1,
      'Name': student.name,
      'Faculty': student.faculty,
      'Year/Semester': student.year,
      'Phone': student.phone,
      'Preference': student.pref,
      'Type': 'Second Preference'
    }));

    // Combine both preferences
    const data = [...firstPrefData, ...secondPrefData];

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);

    // Set column widths
    const wscols = [
      { wch: 5 },  // S.No
      { wch: 25 }, // Name
      { wch: 30 }, // Faculty
      { wch: 15 }, // Year/Semester
      { wch: 15 }, // Phone
      { wch: 30 }, // Preference
      { wch: 15 }, // Type
    ];
    ws['!cols'] = wscols;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Category Data');

    // Generate Excel file
    const fileName = `${category.name.replace(/[^\w\s]/gi, '')}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  useEffect(() => {
    const fetchCategoryData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch category data
        const response = await axios.get('https://dev.sliitleo.org/Backend/category_selection.php');
        const data = response.data;
        
        if (data.success) {
          setCategories(data.data);
          setTotalStudents(data.totalStudents);
        } else {
          throw new Error(data.error);
        }
      } catch (err) {
        setError('Failed to load category data: ' + err.message);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryData();
  }, []);

  // Compute statistics
  const now = new Date();
  const total = totalStudents;

  // Registration year-based groups
  const regYears = [];
  const currentYear = now.getFullYear();
  const prevYears = [currentYear, currentYear-1, currentYear-2, currentYear-3];
  const yearCounts = prevYears.map(y => 0);
  const previousYearsCount = 0;

  return (
    <div className="flex bg-[#111827] text-white min-h-screen">
      <Sidebar />
      <Topbar user={user} />
      <main className="md:ml-64 ml-0 mt-14 w-full px-2 sm:px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-10">
        <div className="mb-4">
          <div className="backdrop-blur-md bg-gradient-to-r from-[#23272f]/80 to-[#181b20]/80 border border-gray-800 rounded-2xl p-3 sm:p-4 md:p-5 flex items-center gap-3 sm:gap-4 shadow-lg">
            <span className="bg-blue-600 p-2 sm:p-3 rounded-full shadow-lg flex-shrink-0">
              <FontAwesomeIcon icon={faLayerGroup} className="text-xl sm:text-2xl" />
            </span>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-bold tracking-wide text-white truncate">Club Dashboard</h1>
              <p className="text-sm sm:text-base text-gray-400 truncate">Category Selection</p>
            </div>
          </div>
        </div>

        <div className="mb-8">
          <div className="flex flex-wrap gap-3 mt-4">
            <Link to="/club-statistics" className="flex items-center gap-2 bg-[#23272f] px-6 py-2 rounded-xl text-base font-semibold text-white border border-gray-700 shadow hover:scale-105 transition focus:outline-none focus:ring-2 focus:ring-orange-400 group">
                          <span className="text-gray-400 text-lg"><FontAwesomeIcon icon={faChartPie} /></span>
                          Club Statistics
                </Link>
                          <Link to="/reporting-hub" className="flex items-center gap-2 bg-[#23272f] px-6 py-2 rounded-xl text-base font-semibold text-white border border-gray-700 shadow hover:scale-105 transition focus:outline-none focus:ring-2 focus:ring-blue-400 group">
                            <span className="text-blue-400 text-lg"><FontAwesomeIcon icon={faChartBar} /></span>
                            Reporting Hub
                          </Link>
                          <Link to="/member-database" className="flex items-center gap-2 bg-[#23272f] px-6 py-2 rounded-xl text-base font-semibold text-white border border-gray-700 shadow hover:scale-105 transition focus:outline-none focus:ring-2 focus:ring-red-400 group">
                             <span className="text-red-400 text-lg"><FontAwesomeIcon icon={faUsers} /></span>
                             Member Database
                           </Link>
                          <Link to="/category-selection" className="flex items-center gap-2 bg-[#23272f] px-6 py-2 rounded-xl text-base font-semibold text-white border border-gray-700 shadow hover:scale-105 transition focus:outline-none focus:ring-2 focus:ring-green-400 group">
                             <span className="text-green-400 text-lg"><FontAwesomeIcon icon={faListAlt} /></span>
                             Category Selections
                            </Link>
                            <Link to="/tickets-admin" className="flex items-center gap-2 bg-[#23272f] px-6 py-2 rounded-xl text-base font-semibold text-white border border-gray-700 shadow hover:scale-105 transition focus:outline-none focus:ring-2 focus:ring-pink-400 group">
                             <span className="text-pink-400 text-lg"><FontAwesomeIcon icon={faTicketAlt} /></span>
                             Tickets-admin
                            </Link>
          </div>
         </div>
         
              <div className=" rounded-xl      ">
                {/* Stats Cards */}
                <div className="  ">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
                    {/* Total Responses */}
                    <div className="bg-gradient-to-br from-[#232830] to-[#1e2229] rounded-xl p-4 sm:p-5 shadow-lg border border-gray-600 hover:border-blue-500/30 transition-all duration-300 group h-full">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-sm font-medium text-gray-400 mb-1">Total Responses</p>
                          <h3 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">{total}</h3>
                        </div>
                        <div className="p-2 rounded-lg bg-blue-500/10 group-hover:bg-blue-500/20 transition-colors">
                          <FaUsers className="text-blue-400 text-xl" />
                        </div>
                      </div>
                      <div className="mt-3 flex items-center text-xs text-blue-400">
                        <span className="inline-block w-2 h-2 rounded-full bg-blue-500 mr-1.5"></span>
                        +12% from last month
                      </div>
                    </div>
                    
                    {/* Active Categories */}
                    <div className="bg-gradient-to-br from-[#232830] to-[#1e2229] rounded-xl p-5 shadow-lg border border-gray-600 hover:border-green-500/30 transition-all duration-300 group">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-sm font-medium text-gray-400 mb-1">Active Categories</p>
                          <h3 className="text-3xl font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">{categories.length}</h3>
                        </div>
                        <div className="p-2 rounded-lg bg-green-500/10 group-hover:bg-green-500/20 transition-colors">
                          <FaLayerGroup className="text-green-400 text-xl" />
                        </div>
                      </div>
                      <div className="mt-3 flex items-center text-xs text-green-400">
                        <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1.5"></span>
                        5 new this month
                      </div>
                    </div>
                    
                    {/* Inactive Categories */}
                    <div className="bg-gradient-to-br from-[#232830] to-[#1e2229] rounded-xl p-5 shadow-lg border border-gray-600 hover:border-red-500/30 transition-all duration-300 group">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-sm font-medium text-gray-400 mb-1">Inactive Categories</p>
                          <h3 className="text-3xl font-bold bg-gradient-to-r from-red-400 to-pink-400 bg-clip-text text-transparent">
                            {categories.filter(cat => 
                              (!cat.firstPref || cat.firstPref.length === 0) && 
                              (!cat.secondPref || cat.secondPref.length === 0)
                            ).length}
                          </h3>
                        </div>
                        <div className="p-2 rounded-lg bg-red-500/10 group-hover:bg-red-500/20 transition-colors">
                          <FaHourglassHalf className="text-red-400 text-xl" />
                        </div>
                      </div>
                      <div className="mt-3 flex items-center text-xs text-red-400">
                        <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-1.5"></span>
                        {categories.some(cat => 
                          (!cat.firstPref || cat.firstPref.length === 0) && 
                          (!cat.secondPref || cat.secondPref.length === 0)
                        ) ? 'Some categories need attention' : 'All categories active'}
                      </div>
                    </div>
                    
                    {/* Trending Categories */}
                    <div className="bg-gradient-to-br from-[#232830] to-[#1e2229] rounded-xl p-5 shadow-lg border border-gray-600 hover:border-purple-500/30 transition-all duration-300 group">
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="text-base font-semibold text-white">Trending Now</h4>
                        <div className="p-2 rounded-lg bg-purple-500/10 group-hover:bg-purple-500/20 transition-colors">
                          <FaChartPie className="text-purple-400 text-lg" />
                        </div>
                      </div>
                      <ul className="space-y-2">
                        {categories
                          .sort((a, b) => {
                            const aFirst = a.firstPref.length;
                            const bFirst = b.firstPref.length;
                            return bFirst - aFirst;
                          })
                          .slice(0, 3)
                          .map((cat, index) => {
                            const color = index === 0 ? 'blue' : index === 1 ? 'purple' : 'green';
                            const percentage = Math.round((cat.firstPref.length / totalStudents) * 100);
                            
                            return (
                              <li key={cat.id} className="flex items-center text-sm text-gray-300 hover:text-white transition-colors">
                                <span className={`w-1.5 h-1.5 rounded-full bg-${color}-400 mr-2`} />
                                <span className="truncate">{cat.name}</span>
                                <span className={`ml-auto text-xs bg-${color}-900/50 text-${color}-300 px-2 py-0.5 rounded-full`}>
                                  {percentage}% ({cat.firstPref.length} First Pref.)
                                </span>
                              </li>
                            );
                          })}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Category Sections */}
                <div className="space-y-4 sm:space-y-6 md:space-y-8 mt-4">
                  {categories.map((category) => (
                    <div key={category.id} className="bg-[#1e2229] overflow-hidden shadow-lg border border-gray-700 rounded-xl p-3 sm:p-4 md:p-5">
                      <div className="p-6 md:p-8">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 sm:mb-6">
                          <h3 className="text-base sm:text-lg font-semibold text-white break-words">{category.name}</h3>
                          <div className="flex items-center gap-2 sm:gap-4">
                            <div className="text-xs sm:text-sm text-gray-400">{category.stats}</div>
                            <button 
                              onClick={() => exportToExcel(category)}
                              className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded-md transition-colors"
                              title="Export to Excel"
                            >
                              <FaFileExcel className="mr-1" />
                              <span>Export</span>
                            </button>
                          </div>
                        </div>
                        
                        <div className="flex flex-col lg:flex-row gap-4 md:gap-6">
                          {/* First Preference Column */}
                          <div className="flex-1">
                            <div className="border-b border-gray-700 pb-2 mb-4">
                              <h4 className="text-md font-medium text-green-400">First Preference | {category.name.split('(')[0].trim()}</h4>
                            </div>
                            <div className="space-y-4">
                              {category.firstPref.length > 0 ? (
                                category.firstPref.map((student, index) => (
                                  <div key={`first-${index}`} className="bg-[#23272f] p-3 sm:p-4 rounded-lg">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-2 sm:space-y-0">
                                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-600 flex-shrink-0 flex items-center justify-center text-white">
                                        <FaUserPlus className="text-sm" />
                                      </div>
                                      <div className="flex-1 min-w-0 w-full">
                                        <div className="font-medium text-white">{student.name} | {student.student_id}</div>
                                        <div className="text-xs text-gray-400">{student.faculty} | {student.year}</div>
                                         {student.secondPref && (
                                          <div className="text-xs text-purple-400 mt-1">2nd: {student.secondPref}</div>
                                        )}
                                      </div>
                                      <div className="text-xs sm:text-sm text-gray-400 mt-1 sm:mt-0">
                                        {student.phone}
                                        <button className="ml-2 text-blue-400">
                                          <FaLink className="inline" />
                                        </button>
                                      </div>
                                    </div>
                                    <div className="text-xs text-gray-500 mt-2">
                                      Submitted on: {student.created_at ? new Date(student.created_at).toLocaleString('en-US', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: true
                                      }) : 'Date not available'}
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="text-center text-xs sm:text-sm text-gray-500 py-3 sm:py-4">No first preferences yet</div>
                              )}
                            </div>
                          </div>
                          
                          {/* Second Preference Column */}
                          <div className="flex-1">
                            <div className="border-b border-gray-700 pb-2 mb-4">
                              <h4 className="text-md font-medium text-purple-400">Second Preference | {category.name.split('(')[0].trim()}</h4>
                            </div>
                            <div className="space-y-4">
                              {category.secondPref.length > 0 ? (
                                category.secondPref.map((student, index) => (
                                  <div key={`second-${index}`} className="bg-[#23272f] p-4 rounded-lg">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-2 sm:space-y-0">
                                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-600 flex-shrink-0 flex items-center justify-center text-white">
                                        <FaUserPlus className="text-sm" />
                                      </div>
                                      <div className="flex-1 min-w-0 w-full">
                                        <div className="font-medium text-white">{student.name} | {student.student_id}</div>
                                        <div className="text-xs text-gray-400">{student.faculty} | {student.year}</div>
                                        {student.firstPref && (
                                          <div className="text-xs text-green-400 mt-1">1st: {student.firstPref}</div>
                                        )}
                                      </div>
                                      <div className="text-xs sm:text-sm text-gray-400 mt-1 sm:mt-0">
                                        {student.phone}
                                        <button className="ml-2 text-blue-400">
                                          <FaLink className="inline" />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="text-center text-xs sm:text-sm text-gray-500 py-3 sm:py-4">No second preferences yet</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                ))}
              </div>
            </div>
          







         

      </main>
    </div>
  );
}
